import { useMemo } from 'react'
import { Grid, Box, Text } from '@chakra-ui/react'
import { useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import ubigeoData from '../data/ubigeo.json'

interface UbigeoSelectProps {
  value: string
  onChange: (code: string) => void
  disabled?: boolean
  required?: boolean
  hideLabel?: boolean
}

type UbigeoJson = Record<string, {
  name: string
  provinces: Record<string, {
    name: string
    districts: Record<string, { name: string }>
  }>
}>

const data = ubigeoData as UbigeoJson

export default function UbigeoSelect({ value, onChange, disabled, required, hideLabel }: UbigeoSelectProps) {
  const c = useFormColors()
  const { t } = useT()

  const selectedDepto = value?.substring(0, 2) || ''
  const selectedProv = value?.substring(0, 4) || ''
  const selectedDist = value?.length === 6 ? value : ''

  const departamentos = useMemo(() => {
    return Object.entries(data)
      .map(([code, d]) => ({ code, name: d.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [])

  const provincias = useMemo(() => {
    if (!selectedDepto || !data[selectedDepto]) return []
    return Object.entries(data[selectedDepto].provinces)
      .map(([code, p]) => ({ code, name: p.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [selectedDepto])

  const distritos = useMemo(() => {
    if (!selectedDepto || !selectedProv) return []
    const prov = data[selectedDepto]?.provinces[selectedProv]
    if (!prov) return []
    return Object.entries(prov.districts)
      .map(([code, d]) => ({ code, name: d.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [selectedDepto, selectedProv])

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 1rem',
    borderRadius: '0.25rem',
    border: 'none',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
  }

  const chevronIcon = (
    <Box position="absolute" right="0.75rem" top="50%" transform="translateY(-50%)" pointerEvents="none" color={c.textSecondary}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </Box>
  )

  const handleDeptoChange = (code: string) => {
    onChange('')
    if (code) {
      onChange('')
    }
    onChange(code)
  }

  const handleProvChange = (code: string) => {
    if (code) {
      onChange(code)
    } else {
      onChange(selectedDepto)
    }
  }

  const handleDistChange = (code: string) => {
    if (code) {
      onChange(code)
    } else {
      onChange(selectedProv)
    }
  }

  const placeholder = t('shared.ubigeo.selectPlaceholder')

  return (
    <Box>
      {!hideLabel && (
        <Text fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2}>
          {t('shared.ubigeo.label')}{required && <Text as="span" color="red.400"> *</Text>}
        </Text>
      )}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
        <Box>
          <Text tid="shared.ubigeo.departamento" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
          <Box position="relative">
            <select
              value={selectedDepto}
              onChange={(e) => handleDeptoChange(e.target.value)}
              style={selectStyles}
              disabled={disabled}
            >
              <option value="">{placeholder}</option>
              {departamentos.map((d) => (
                <option key={d.code} value={d.code}>{d.name}</option>
              ))}
            </select>
            {chevronIcon}
          </Box>
        </Box>

        <Box>
          <Text tid="shared.ubigeo.provincia" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
          <Box position="relative">
            <select
              value={selectedProv}
              onChange={(e) => handleProvChange(e.target.value)}
              style={selectStyles}
              disabled={disabled || !selectedDepto}
            >
              <option value="">{placeholder}</option>
              {provincias.map((p) => (
                <option key={p.code} value={p.code}>{p.name}</option>
              ))}
            </select>
            {chevronIcon}
          </Box>
        </Box>

        <Box>
          <Text tid="shared.ubigeo.distrito" fontSize="sm" fontWeight="500" color={c.textSecondary} mb={2} />
          <Box position="relative">
            <select
              value={selectedDist}
              onChange={(e) => handleDistChange(e.target.value)}
              style={selectStyles}
              disabled={disabled || !selectedProv}
            >
              <option value="">{placeholder}</option>
              {distritos.map((d) => (
                <option key={d.code} value={d.code}>{d.name}</option>
              ))}
            </select>
            {chevronIcon}
          </Box>
        </Box>
      </Grid>
    </Box>
  )
}
